import type { CSSProperties, HTMLAttributes, PropsWithChildren } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import useMergedRef from '@react-hook/merged-ref';
import type { Rect } from 'react-use-rect';
import { useRect } from 'react-use-rect';
import { Portal } from '../portal';
import { zIndexLayer } from '../zIndexLayer';

type barMode = 'static' | 'floating-top' | 'floating-bottom';
interface BarProps extends PropsWithChildren, HTMLAttributes<HTMLDivElement> {
  mode?: barMode;
  portalElement?: HTMLDivElement;
  className?: string;
  style?: CSSProperties;
  shadow?: boolean;
}

export const Bar = forwardRef<HTMLDivElement, BarProps>(function (
  {
    children,
    mode = 'static',
    portalElement,
    className,
    style,
    shadow,
    ...forwardingProps
  },
  forwardRef
) {
  const [rect, setRect] = useState<Rect | null>(null);
  const [rectRef] = useRect(setRect, { resize: true });

  useEffect(() => {
    document.body.style.position = 'relative';

    return () => {
      document.body.style.position = '';
    };
  }, []);

  useEffect(() => {
    const html = document.querySelector('html');
    if (html) {
      if (mode === 'floating-bottom') {
        html.style.marginBottom = `${rect?.height}px`;
      } else {
        html.style.marginTop = `${rect?.height}px`;
      }
    }

    return () => {
      if (html) {
        html.style.marginTop = '';
        html.style.marginBottom = '';
      }
    };
  }, [rect?.height, mode]);

  const mergedRef = useMergedRef(rectRef, forwardRef);

  return (
    <Portal containerElement={portalElement} shadow={shadow}>
      <BarContainer
        {...forwardingProps}
        ref={mergedRef}
        style={style}
        className={className}
        _mode={mode}
        _offset={rect?.height}
      >
        {children}
      </BarContainer>
    </Portal>
  );
});

const BarContainer = styled.div<{
  _position?: string;
  _offset?: number;
  _mode?: barMode;
}>`
  z-index: ${zIndexLayer.modal};

  width: 100%;
  height: max-content;
  left: 0;

  position: ${({ _mode = 'static' }) => cssPositions[_mode]};
  top: ${({ _offset = 0, _mode = 'static' }) => {
    if (_mode === 'static') return `${-_offset}px`;
    if (_mode === 'floating-top') return 0;
    return null;
  }};

  bottom: ${({ _mode = 'static' }) => {
    if (_mode === 'floating-bottom') return 0;
    return null;
  }};
`;

const cssPositions = {
  'floating-bottom': 'fixed',
  'floating-top': 'fixed',
  static: 'absolute'
};
